import { ApiFragment } from '../entities/api-fragment.entity';
import { HierarchyRow, toSubmapRow } from '../entities/hierarchy-row.entity';
import { normalizePath, toPathCaption } from './path-caption.helper';

/**
 * How a submap's folders are joined into the one string a header shows and a group is keyed by.
 *
 * It has to be the separator `toSpacedPathCaption` splits on, because that is what typesets the header — a
 * different character here and the header would render as one unbroken folder name while a per-row caption of
 * the same path renders with air around its separators.
 */
export const PATH_SEPARATOR: string = '/';

/**
 * HFBD-74, revised by HFBD-6 decision 11: the folder a stuk sits in inside its dossier, empty for one sitting
 * at the dossier root.
 *
 * Reads `Dynamic.DocumentPath` — the whole path, slash-joined — through {@link toPathCaption}, so the header
 * above a group and the caption on a row can never name different folders for one stuk. The pre-segmented
 * `Dynamic.DocumentPathTags` list is deliberately **not** read: `edepot-backend` round-trips the two fields
 * separately and the entity comment says to read one of them and never both, and HFBD-20's shipped comment
 * records that the segmented list *"isn't always in the same left-to-right order as Full"*. A joined string
 * cannot be mis-ordered; a list can.
 *
 * The path's shape is a **contract**, confirmed by the product owner on 2026-08-06: the first segment is the
 * dossier, the last is the filename, and the segments between them are the submappen the informatiebeheerder
 * made. So both ends come off, and neither strip is a "does this look like a file" test.
 *
 * ⚠ `dropDossierSegment` is **per surface**, not global. A page listing **many** dossiers must keep the
 * dossier segment, or a `2024` folder in dossier A merges with a `2024` in dossier B — the merge
 * {@link groupBySubmap}'s key already refuses to cause. Only a page scoped to one dossier may drop it, and
 * that is the same set of surfaces that gets the Submap filter.
 *
 * ⚠ **Each segment is trimmed.** The shared URL parser `.trim()`s every facet value it parses, so an
 * untrimmed key would leave a selected folder in the filter panel *and* as a pill at the same time. Two
 * folders differing only by outer whitespace therefore merge; that loss is accepted. Case is left alone —
 * whether `Bestek` and `bestek` are one folder is ingest's call and the source filesystem's.
 */
export function toSubmapPath(record: ApiFragment, options: { dropDossierSegment: boolean }): string {
    const path: string | null = toPathCaption(record);

    return path === null ? '' : toSubmapSegments(path, options).join(PATH_SEPARATOR);
}

/**
 * The folders in a slash-joined path, as {@link toSubmapPath} reads them — the same rule, exposed so the
 * Submap filter's tree and the submap header rows cannot drift apart. See that function for why each end of
 * the path comes off, why `dropDossierSegment` is per surface, and why every segment is trimmed.
 *
 * ⚠ Splitting on `/` is a guess, and it is unavoidable: a folder name holding a slash splits wrong. The
 * pre-segmented field has no such problem but cannot express a path at all, and the wiki states it plainly —
 * *"there is no third option"*.
 */
export function toSubmapSegments(path: string, options: { dropDossierSegment: boolean }): string[] {
    return normalizePath(path)
        .split(PATH_SEPARATOR)
        .map(segment => segment.trim())
        .slice(options.dropDossierSegment ? 1 : 0, -1);
}

/**
 * Shows each submap once, on a header row above the stukken it holds, instead of captioning it on every one of
 * them. A **stable partition** of rows that are already sorted, so it adds structure without taking the
 * ordering away: group order is first appearance under the inherited sort, and within a group the rows come out
 * exactly as they went in.
 *
 * **There is no comparator and no sort call here, and there must not be one.** A `Map` preserves insertion
 * order, which is what makes both of those orderings free; sorting by path would instead override the sort the
 * user chose, and the children request cannot express a two-key sort to do it server-side either.
 *
 * `grouped: false` passes the rows straight through, untouched. That is the contract for every state a group
 * cannot honestly be drawn in — a partially-loaded expansion, and a tree page's *flattened* mode, whose rows
 * come from many dossiers at once while the key holds no dossier.
 *
 * HFBD-85 gave it a second frame: a **natively flat** detail page groups its top-level rows, which have no
 * parent key. The `?? ''` fallback below is what that reaches, and the resulting header key carries an empty
 * middle segment — `submap::<path>` — which is correct rather than a bug.
 *
 * Stukken sharing no folder — at the dossier root, or with the field absent — get no header and no new visible
 * string: an empty path names nothing, and a caption would print the filename this strips.
 *
 * Keys are compared **exactly**, on the segments it is given: no trimming, no case folding, no collapsing
 * here. ⚠ Outer whitespace no longer reaches this point — {@link toSubmapPath} trims each segment for
 * HFBD-6 — but case still does, and whether `Bestek` and `bestek` are one folder is ingest's call and the
 * source filesystem's, not the frontend's. Comparing exactly can only ever over-split, which costs one
 * duplicate header and is visible; merging two folders the archive calls distinct would be silent and
 * unrecoverable.
 */
export function groupBySubmap<T>(
    childRows: readonly HierarchyRow<T>[],
    options: { grouped: boolean }
): HierarchyRow<T>[] {
    if (!options.grouped) {
        return [...childRows];
    }

    const groups = new Map<string, HierarchyRow<T>[]>();

    for (const row of childRows) {
        const path: string = row.submap ?? '';
        const group: HierarchyRow<T>[] = groups.get(path) ?? [];

        group.push({ ...row, grouped: true });
        groups.set(path, group);
    }

    return [...groups].flatMap(([path, group]) =>
        path.length > 0 ? [toSubmapRow<T>(group[0].parentKey ?? '', path, group[0].level), ...group] : group
    );
}
